/**
 * workflowSession.ts — Workflow 会话顶层状态
 *
 * 职责：
 *     定义 /workflow 页专用 WorkflowSessionState（与 /chat SessionTree 隔离）。
 *
 * 链路位置：
 *     workflowReducer → workflowSessionStore → WorkflowSession / WorkflowChrome。
 *
 * 当前裁剪范围：
 *     不含合并 TaskListFooter（P3）；parallel 结构仅基础字段。
 */
import type { SessionStatus, ReducerError, SessionTree } from './tree';
import {
  createEmptyWorkflowContainerTreeState,
  type WorkflowContainerTreeState,
} from './workflowContainer';

/** P2：stage 完成摘要缓存（loop 树淘汰后仍显示 teaser） */
export interface CachedStageDoneSummary {
  label: string;
  teaser: string;
  extraLines: number;
}

export type WorkflowRunStatus = 'running' | 'done' | 'error';

export interface WorkflowStageProgress {
  stageKey: string;
  stageIndex: number;
  stageTotal?: number;
  status: 'pending' | 'running' | 'done' | 'failed';
  error?: string;
}

export interface ParallelItemProgress {
  itemKey: string;
  itemIndex: number;
  itemTotal?: number;
  status: 'pending' | 'running' | 'done' | 'failed';
  loopSessionId: string | null;
  error?: string;
}

export interface WorkflowProgressState {
  workflowId: string | null;
  workflowPath: string | null;
  status: WorkflowRunStatus;
  stages: WorkflowStageProgress[];
  parallelItems: Record<string, ParallelItemProgress>;
}

export type WorkflowScopeKind = 'stage' | 'parallel-item' | 'subworkflow';

export interface WorkflowScopeFrame {
  kind: WorkflowScopeKind;
  key: string;
  loopSessionId: string | null;
}

export interface WorkflowSessionState {
  status: SessionStatus;
  meta: {
    workflowRunId: string | null;
    startedAt: number | null;
    lastEventId: string | null;
  };
  /** SDK 容器树投影 + loopSessionId 绑定（P1 主模型） */
  containerTree: WorkflowContainerTreeState;
  /** 过渡期 dual-write；P2+ UI 逐步改读 containerTree */
  workflowProgress: WorkflowProgressState;
  loopTreesBySessionId: Record<string, SessionTree>;
  /** P2：container_id → done 摘要（loop 淘汰后） */
  stageDoneSummariesByContainerId: Record<string, CachedStageDoneSummary>;
  /** P2：completed hydrated loop LRU 热缓存（最近使用在数组尾部） */
  hotCompletedLoopSessionIds: string[];
  activeLoopSessionId: string | null;
  /** @deprecated P1 起由 containerTree.activeContainerIds 替代 */
  openScopeStack: WorkflowScopeFrame[];
  internalErrors: ReducerError[];
}

export function createEmptyWorkflowProgress(): WorkflowProgressState {
  return {
    workflowId: null,
    workflowPath: null,
    status: 'running',
    stages: [],
    parallelItems: {},
  };
}

export function createEmptyWorkflowSessionState(): WorkflowSessionState {
  return {
    status: 'connecting',
    meta: { workflowRunId: null, startedAt: null, lastEventId: null },
    containerTree: createEmptyWorkflowContainerTreeState(),
    workflowProgress: createEmptyWorkflowProgress(),
    loopTreesBySessionId: {},
    stageDoneSummariesByContainerId: {},
    hotCompletedLoopSessionIds: [],
    activeLoopSessionId: null,
    openScopeStack: [],
    internalErrors: [],
  };
}
